import React, { useState } from 'react';
import { Collapse, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  UrgencyLevel,
  urgencyStyles,
  BannerContainer,
  TopRow,
  ContentWrapper,
  IconWrapper,
  TextContent,
  DaysText,
  SubText,
  ButtonRow,
  ActionsWrapper,
  PurchaseButton,
  CloseButton,
} from './TrialStatusBanner.styles';
import { UsageStatsDisplay } from './UsageStatsDisplay';
import { SALES_EMAIL } from '../../constants/endpoints';

interface TrialStatusBannerProps {
  daysRemaining: number;
  organizationId?: string;
  onPurchaseClick?: () => void;
  isFullscreen?: boolean;
}

const getUrgencyLevel = (days: number): UrgencyLevel => {
  if (days <= 2) return 'critical';  // Red - urgent action needed
  if (days <= 5) return 'high';      // Orange - warning
  if (days <= 10) return 'medium';   // Blue - reminder
  return 'low';                      // Green - safe, plenty of time
};

const UrgencyIcon: React.FC<{ urgency: UrgencyLevel }> = ({ urgency }) => {
  const iconSx = { fontSize: '18px', color: urgencyStyles[urgency].iconColor };
  
  switch (urgency) {
    case 'critical':
      return <ErrorOutlineIcon sx={iconSx} />;
    case 'high':
      return <WarningAmberIcon sx={iconSx} />;
    default:
      return <AccessTimeIcon sx={iconSx} />;
  }
};

export const TrialStatusBanner: React.FC<TrialStatusBannerProps> = ({
  daysRemaining,
  organizationId,
  onPurchaseClick,
  isFullscreen = false,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(true);
  
  const urgency = getUrgencyLevel(daysRemaining);
  
  const handlePurchaseClick = () => {
    // Open email client to contact sales in a new tab
    window.open(`mailto:${SALES_EMAIL}?subject=AppManager AI License Purchase`, '_blank');
    onPurchaseClick?.();
  };
  
  const getDaysText = () => {
    if (daysRemaining === 0) {
      return t('trialBannerLastDay');
    }
    if (daysRemaining === 1) {
      return t('trialBannerOneDay');
    }
    return t('trialBannerDays', { days: daysRemaining });
  };
  
  const getSubText = () => {
    switch (urgency) {
      case 'critical':
        return t('trialBannerCritical');
      case 'high':
        return t('trialBannerHigh');
      case 'medium':
        return t('trialBannerMedium');
      default:
        return t('trialBannerLow');
    }
  };

  return (
    <Collapse in={isVisible}>
      <BannerContainer
        urgency={urgency}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      >
        <TopRow>
          <ContentWrapper>
            <IconWrapper urgency={urgency}>
              <UrgencyIcon urgency={urgency} />
            </IconWrapper>
            <TextContent>
              <DaysText urgency={urgency}>
                {getDaysText()}
              </DaysText>
              <SubText urgency={urgency}>
                {getSubText()}
              </SubText>
            </TextContent>
          </ContentWrapper>
          
          {isFullscreen && (
            <ActionsWrapper>
              <Tooltip title={t('trialBannerPurchaseTooltip')} placement="bottom">
                <PurchaseButton urgency={urgency} onClick={handlePurchaseClick}>
                  {t('trialBannerUpgrade')}
                  <ArrowForwardIcon sx={{ fontSize: '16px' }} />
                </PurchaseButton>
              </Tooltip>
              <UsageStatsDisplay 
                organizationId={organizationId} 
                variant="compact"
                daysRemaining={daysRemaining}
              />
            </ActionsWrapper>
          )}
          
          <Tooltip title={t('trialBannerDismiss')} placement="bottom">
            <CloseButton 
              urgency={urgency} 
              onClick={() => setIsVisible(false)}
              size="small"
              aria-label={t('trialBannerDismiss')}
            >
              <CloseIcon sx={{ fontSize: '16px' }} />
            </CloseButton>
          </Tooltip>
        </TopRow>
        
        {!isFullscreen && (
          <ButtonRow>
            <Tooltip title={t('trialBannerPurchaseTooltip')} placement="bottom">
              <PurchaseButton urgency={urgency} onClick={handlePurchaseClick}>
                {t('trialBannerUpgrade')}
                <ArrowForwardIcon sx={{ fontSize: '16px' }} />
              </PurchaseButton>
            </Tooltip>
            
            {/* Usage Stats Icon with Tooltip */}
            <UsageStatsDisplay 
              organizationId={organizationId} 
              variant="compact"
              daysRemaining={daysRemaining}
            />
          </ButtonRow>
        )}
      </BannerContainer>
    </Collapse>
  );
};
